import type { Router } from '@angular/router';
import { NavigationEnd, NavigationStart } from '@angular/router';
import { filter } from 'rxjs/operators';
import { z } from 'zod';

// Module-level state accessible from beforeSend
let lastNavigationTarget: string | null = null;
let appBasePath = '/';

const STORAGE_KEY = 'chunkLoadErrorRetry';
const MAX_RETRIES = 2;

const RetryDataSchema = z.object({
	url: z.string(),
	count: z.number(),
});
type RetryDataType = z.infer<typeof RetryDataSchema>;

/**
 * Converts a router-relative URL to an absolute URL using the app's base path.
 *
 * Router URLs like '/admin/schedules' need to be combined with the base path.
 * For base path '/smartpass' and URL '/admin/schedules', result should be:
 * 'https://host.com/smartpass/admin/schedules'
 */
function toAbsoluteUrl(routerUrl: string): string {
	// Remove leading slash from router URL to avoid double slashes
	const path = routerUrl.startsWith('/') ? routerUrl.slice(1) : routerUrl;
	// Ensure base path ends with slash
	const base = appBasePath.endsWith('/') ? appBasePath : appBasePath + '/';
	return window.location.origin + base + path;
}

/**
 * Initialize navigation tracking. Call this from APP_INITIALIZER.
 * Updates lastNavigationTarget whenever navigation starts.
 *
 * @param router Angular Router instance
 * @param basePath App base path from config (e.g., '/' or '/app/')
 */
export function initNavigationTracker(router: Router, basePath: string): void {
	appBasePath = basePath;

	router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe((event) => {
		// Convert relative URL to absolute using base path
		lastNavigationTarget = toAbsoluteUrl(event.url);
	});

	// Clear retry storage on successful navigation
	router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(() => {
		sessionStorage.removeItem(STORAGE_KEY);
	});
}

/**
 * Check if an error is a chunk load error.
 */
export function isChunkLoadError(error: unknown): boolean {
	if (!error) return false;
	if (error instanceof Error) {
		return (
			error.message.includes('ChunkLoadError') || // Angular
			error.message.includes('Failed to fetch dynamically imported module') || // Chrome
			error.message.includes('Importing a module script failed.') || // Safari
			error.message.includes('error loading dynamically imported module') // Firefox
		);
	}
	return false;
}

/**
 * Handle chunk load error. Returns true if handled (should filter from Sentry).
 * Called from Sentry's beforeSend.
 *
 * When a deployment occurs while a user has an old version of the app open,
 * the old chunk files may no longer be available (evicted from Cloudflare cache).
 * This causes a ChunkLoadError when navigating to a lazy-loaded route.
 *
 * This function performs a hard navigation to reload the app with fresh chunks.
 * A retry guard prevents infinite reload loops if chunks keep failing.
 */
export function handleChunkLoadError(error: unknown): boolean {
	if (!isChunkLoadError(error)) {
		return false;
	}

	// Use tracked navigation target (absolute URL) or fall back to current location
	const targetUrl = lastNavigationTarget ?? window.location.href;

	// Check retry count
	const retryData = sessionStorage.getItem(STORAGE_KEY);
	const parsed = retryData ? RetryDataSchema.safeParse(JSON.parse(retryData)) : null;
	const { url, count } = parsed?.success ? parsed.data : { url: null, count: 0 };
	const currentCount = url === targetUrl ? count : 0;

	if (currentCount >= MAX_RETRIES) {
		console.error('[ChunkLoadError] Max retries exceeded for route:', targetUrl);
		sessionStorage.removeItem(STORAGE_KEY);
		// Return false to let Sentry report this - we've given up
		return false;
	}

	// Store retry attempt
	const data: RetryDataType = {
		url: targetUrl,
		count: currentCount + 1,
	};
	sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));

	console.warn('[ChunkLoadError] Failed to load route, reloading:', targetUrl);
	window.location.assign(targetUrl);

	return true; // Handled, filter from Sentry
}
